import type { SiteExportData } from "./data-fetcher";
import { generateExcelBlob } from "./excel-export";
import { generateAnnotatedPlanBlob } from "./plan-export";

/**
 * Sanitise un nom pour l'utiliser comme nom de fichier/dossier dans un ZIP.
 * Retire les caractères interdits dans les systèmes de fichiers.
 */
function sanitizeName(name: string): string {
  return name
    .replace(/[/\\:*?"<>|]/g, "_")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Génère un ZIP complet pour un site :
 * - Excel avec toutes les zones
 * - Plans annotés PNG par zone
 * - Photos organisées par mesure
 *
 * Structure :
 * {SiteName}/
 *   export.xlsx
 *   {ZoneName}/
 *     plan_annoté.png
 *     photos/
 *       M{numero}-P1.jpg
 *       M{numero}-P2.jpg
 */
export async function generateZipBlob(
  data: SiteExportData
): Promise<Blob> {
  const JSZip = (await import("jszip")).default;
  const zip = new JSZip();

  const siteName = sanitizeName(data.site.name);
  const siteFolder = zip.folder(siteName)!;

  // Excel
  const excelBlob = await generateExcelBlob(data);
  siteFolder.file("export.xlsx", excelBlob);

  // Par zone : plan annoté + photos
  for (const zoneData of data.zones) {
    const zoneName = sanitizeName(zoneData.zone.name);
    const zoneFolder = siteFolder.folder(zoneName)!;

    // Plan annoté PNG
    const planBlob = await generateAnnotatedPlanBlob(zoneData);
    if (planBlob) {
      zoneFolder.file("plan_annoté.png", planBlob);
    }

    // Photos par mesure (modèle B : zone → pins directement)
    let hasPhotos = false;
    for (const pinData of zoneData.pins) {
      if (pinData.photos.length > 0 && pinData.mesure) {
        if (!hasPhotos) {
          hasPhotos = true;
        }
        const photosFolder = zoneFolder.folder("photos")!;
        for (let i = 0; i < pinData.photos.length; i++) {
          const photo = pinData.photos[i];
          const fileName = `US${pinData.mesure.numero}-P${i + 1}.jpg`;
          photosFolder.file(fileName, photo.blob);
        }
      }
    }
  }

  return zip.generateAsync({ type: "blob" });
}
